import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid, MenuItem, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { IKoreanUniversity, KoreanUniversityType } from 'Entities/koreanUniversity'
import { useKoreanUniversities } from '../Hooks/useKoreanUniversities'

const isUniversityType = (t: string): t is KoreanUniversityType =>
  t === 'KOREAN_DEPARTMENT' || t === 'SECOND_FOREIGN_LANGUAGE'

const TYPE_OPTIONS: { value: KoreanUniversityType; label: string }[] = [
  { value: 'KOREAN_DEPARTMENT', label: 'Кафедра корейского языка' },
  { value: 'SECOND_FOREIGN_LANGUAGE', label: 'Второй иностранный язык' },
]

type Props = {
  open: boolean
  onClose: () => void
  university: IKoreanUniversity
  regionCode: number
}

export const KoreanUniversityEditDialog = ({ open, onClose, university, regionCode }: Props) => {
  const { updateUniversityMutation } = useKoreanUniversities()

  const [form, setForm] = useState({
    universityNameUz: '',
    universityNameRu: '',
    universityNameKo: '',
    type: 'KOREAN_DEPARTMENT' as KoreanUniversityType,
  })

  const [touched, setTouched] = useState({
    universityNameUz: false,
    universityNameRu: false,
    universityNameKo: false,
    type: false,
  })

  useEffect(() => {
    if (university) {
      setForm({
        universityNameUz: university.universityNameUz ?? '',
        universityNameRu: university.universityNameRu ?? '',
        universityNameKo: university.universityNameKo ?? '',
        type: isUniversityType(String(university.type)) ? (university.type as KoreanUniversityType) : 'KOREAN_DEPARTMENT',
      })
      setTouched({
        universityNameUz: false,
        universityNameRu: false,
        universityNameKo: false,
        type: false,
      })
    }
  }, [university])

  const rawErrors = useMemo(
    () => ({
      universityNameUz: !form.universityNameUz.trim(),
      universityNameRu: !form.universityNameRu.trim(),
      universityNameKo: !form.universityNameKo.trim(),
    }),
    [form],
  )

  const hasError = rawErrors.universityNameUz || rawErrors.universityNameRu || rawErrors.universityNameKo

  const handleChange = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleBlur = (field: keyof typeof touched) => () => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = () => {
    if (hasError) {
      setTouched({
        universityNameUz: true,
        universityNameRu: true,
        universityNameKo: true,
        type: true,
      })
      return
    }

    const prevType: KoreanUniversityType = isUniversityType(String(university.type))
      ? (university.type as KoreanUniversityType)
      : 'KOREAN_DEPARTMENT'

    if (
      form.universityNameKo === university.universityNameKo &&
      form.universityNameRu === university.universityNameRu &&
      form.universityNameUz === university.universityNameUz &&
      form.type === prevType
    ) {
      return toast.warning('Ничего не изменилось!')
    }

    updateUniversityMutation.mutate(
      {
        universityId: university.universityId,
        payload: {
          regionCode,
          universityNameUz: form.universityNameUz.trim(),
          universityNameRu: form.universityNameRu.trim(),
          universityNameKo: form.universityNameKo.trim(),
          type: form.type,
        },
      },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Университет — редактирование</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label='Тип'
              value={form.type}
              onChange={handleChange('type')}
              onBlur={handleBlur('type')}>
              {TYPE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название (UZ)'
              value={form.universityNameUz}
              onChange={handleChange('universityNameUz')}
              onBlur={handleBlur('universityNameUz')}
              error={rawErrors.universityNameUz && touched.universityNameUz}
              helperText={rawErrors.universityNameUz && touched.universityNameUz ? 'Обязательное поле.' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название (RU)'
              value={form.universityNameRu}
              onChange={handleChange('universityNameRu')}
              onBlur={handleBlur('universityNameRu')}
              error={rawErrors.universityNameRu && touched.universityNameRu}
              helperText={rawErrors.universityNameRu && touched.universityNameRu ? 'Обязательное поле.' : ' '}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Название (KO)'
              value={form.universityNameKo}
              onChange={handleChange('universityNameKo')}
              onBlur={handleBlur('universityNameKo')}
              error={rawErrors.universityNameKo && touched.universityNameKo}
              helperText={rawErrors.universityNameKo && touched.universityNameKo ? 'Обязательное поле.' : ' '}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color='inherit'>
          취소
        </Button>
        <Button onClick={handleSubmit} variant='contained' disabled={updateUniversityMutation.isLoading}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  )
}
